<template>
  <form class="trainer-activity-picker" @submit.prevent="submit">
    <div class="trainer-activity-picker__header">
      <h3>Active CPD activities</h3>
      <p>Select an activity to assign to this trainer.</p>
    </div>
    <v-progress-linear v-if="cpdActLoading" indeterminate color="primary" aria-label="Loading active activities" />
    <v-alert v-else-if="assignmentActivities.length === 0" type="info" outlined>
      No active CPD activities are available for your provider.
    </v-alert>
    <v-radio-group
        v-else
        v-model="selectedActivityKey"
        class="trainer-activity-picker__list"
        aria-label="Active CPD activities"
        :disabled="saving"
        hide-details
        @change="selectActivity"
    >
      <v-radio
          v-for="activity in assignmentActivities"
          :key="activity.key"
          :value="activity.key"
          :class="{'trainer-activity-picker__selected': selectedActivityKey === activity.key}"
          color="primary"
      >
        <template v-slot:label>
          <div class="trainer-activity-picker__activity">
            <div class="trainer-activity-picker__name">{{ activity.name }}</div>
            <div class="trainer-activity-picker__category">
              {{ activity.categoryName }} · {{ activity.categoryType === 'CAT1' ? 'CAT I' : 'CAT II' }}
            </div>
            <div class="trainer-activity-picker__dates">
              {{ formatActivityDate(activity.startDate) }} – {{ formatActivityDate(activity.endDate) }}
            </div>
          </div>
        </template>
      </v-radio>
    </v-radio-group>
    <div class="trainer-activity-picker__actions">
      <v-btn type="submit" color="primary" depressed :loading="saving"
             :disabled="saving || !hasAssignableSelection">
        Assign Trainer
      </v-btn>
    </div>
  </form>
</template>

<script>
import {getProgramsLite, getUser} from "@/utils/helper";
import {canAssignTrainerActivity} from "@/utils/trainerAssignment";

export default {
  props: {
    trainer: Object,
    saving: {type: Boolean, default: false},
  },
  data() {
    return {
      cpdActLoading: false,
      CPD_ACTIVITIES: [],
      selectedActivityKey: null,
      formValues: {
        cpdActivityId: null,
        course: null,
        category2Course: null,
        assigningCourse: true,
        editMode: false,
        previousCourseId: null,
        previousCourseCategory: null,
      },
    };
  },
  async created() {
    await this.initLookups();
    if (this.trainer) {
      this.formValues.editMode = true;
      this.formValues.previousCourseId = this.trainer.id;
      this.formValues.previousCourseCategory = this.trainer.program?.category?.categoryType;
      const current = this.assignmentActivities.find(activity =>
          Number(activity.programId) === Number(this.trainer.programId) &&
          (activity.categoryType !== "CAT1" ||
              Number(activity.course.courseId) === Number(this.trainer.courseId ?? this.trainer.course?.id)));
      if (current) this.selectActivity(current.key);
    }
  },
  computed: {
    assignmentActivities() {
      return this.CPD_ACTIVITIES.flatMap(program => {
        const details = {
          programId: program.id,
          categoryId: program.categoryId,
          categoryName: program.category?.name || "CPD activity",
          categoryType: program.category?.categoryType,
          startDate: program.startDate,
          endDate: program.endDate,
        };
        if (details.categoryType === "CAT1") {
          return (program.program_courses || []).filter(item => item.course && item.courseId).map(item => ({
            ...details,
            key: `CAT1-${program.id}-${item.courseId}`,
            name: item.course.courseName,
            course: {...item, programId: program.id},
          }));
        }
        if (details.categoryType === "CAT2") {
          return [{...details, key: `CAT2-${program.id}`, name: program.courseTitleCat2 || details.categoryName}];
        }
        return [];
      });
    },
    hasAssignableSelection() {
      return this.isSelectedActivityAssignable();
    },
  },
  methods: {
    async initLookups() {
      this.cpdActLoading = true;
      try {
        const response = await getProgramsLite();
        this.CPD_ACTIVITIES = (response?.data?.rows || []).filter(program => canAssignTrainerActivity(program, getUser()));
      } finally {
        this.cpdActLoading = false;
      }
    },
    formatActivityDate(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    selectActivity(key) {
      const activity = this.assignmentActivities.find(item => item.key === key);
      this.selectedActivityKey = activity?.key || null;
      this.formValues.cpdActivityId = activity?.categoryId || null;
      this.formValues.course = activity?.course || null;
      this.formValues.category2Course = activity?.categoryType === "CAT2" ? activity.programId : null;
    },
    isSelectedActivityAssignable() {
      const activity = this.assignmentActivities.find(item => item.key === this.selectedActivityKey);
      const program = this.CPD_ACTIVITIES.find(item => Number(item.id) === Number(activity?.programId));
      return Boolean(program && canAssignTrainerActivity(program, getUser()));
    },
    submit() {
      if (this.saving) return;
      if (!this.isSelectedActivityAssignable()) {
        this.$toast.error("Select an active CPD activity owned by your provider.");
        return;
      }
      this.$emit("saveAssignedCourse", {...this.formValues});
    },
  },
};
</script>

<style scoped>
.trainer-activity-picker {
  padding: 24px;
}

.trainer-activity-picker__header h3 {
  margin: 0 0 4px;
  font-size: 16px;
  color: #0f172a;
}

.trainer-activity-picker__header p {
  margin: 0 0 16px;
  color: #64748b;
}

.trainer-activity-picker__list {
  margin-top: 0;
  padding-top: 0;
}

.trainer-activity-picker__list ::v-deep .v-radio {
  align-items: flex-start;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
}

.trainer-activity-picker__list ::v-deep .v-label {
  min-width: 0;
}

.trainer-activity-picker__list ::v-deep .trainer-activity-picker__selected {
  border-color: #1976d2;
  background: #eff6ff;
}

.trainer-activity-picker__name {
  color: #0f172a;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.trainer-activity-picker__category,
.trainer-activity-picker__dates {
  color: #64748b;
  font-size: 13px;
  margin-top: 4px;
}

.trainer-activity-picker__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
